/**
 * Block 14 «Раскрой» — PR #6a-PARTS (CUT-2.3/2.5/2.6). Чистые утилиты таблицы
 * деталей: пустая деталь, полнота, подсчёт, объединение одинаковых (CUT-2.5),
 * валидация «деталь > листа» (§5.1.10).
 *
 * Логика валидации — вербатим `js/table-features.js:validatePartRow` + TRD §5.1.10:
 * полезная зона = размер листа − 2×обпил; деталь помещается если влезает хотя бы в
 * одной из двух ориентаций. В mixed-режиме сравнение с МАКСИМАЛЬНЫМ листом из вариантов.
 *
 * @see Помощник — Прототип Раскроя/03-tz-developer/03-TRD.md §5.1.10
 */

import { genUuid } from '@/lib/uuid';
import type { EdgeAssignment, GrooveAssignment, Part, SheetSizeConfig } from './types';

/** Новая пустая строка деталей (0×0, qty 1, без поворота/кромок/пазов/обработки). */
export function makeEmptyPart(): Part {
  return {
    id: genUuid(), // genUuid: фолбэк для insecure-context/старых WebView (Bug G)
    length: 0,
    width: 0,
    qty: 1,
    rotation: 'forbid',
    name: '',
    do: false,
    edges: { top: '', bottom: '', left: '', right: '' },
    grooves: { top: '', right: '', bottom: '', left: '' }, // PR #10b-1-GROOVE
    processing: '', // PR #10b-2 — без обработки
  };
}

/** Деталь заполнена (имеет положительные размеры) — годна для контракта/раскроя. */
export function isCompletePart(p: Part): boolean {
  return p.length > 0 && p.width > 0;
}

/** Всего деталей — сумма qty всех строк (футер таблицы «Деталей: N»). */
export function countParts(parts: Part[]): number {
  return parts.reduce((sum, p) => sum + (p.qty || 0), 0);
}

/** Стабильный ключ кромок (для сравнения при объединении). */
function edgesKey(e: EdgeAssignment): string {
  return `${e.top}|${e.right}|${e.bottom}|${e.left}`;
}

/** Стабильный ключ пазов (PR #10b-1: пазы — часть идентичности детали). */
function groovesKey(g: GrooveAssignment): string {
  return `${g.top}|${g.right}|${g.bottom}|${g.left}`;
}

/**
 * Объединить одинаковые детали (CUT-2.5): строки с одинаковыми
 * Длина+Ширина+Наименование+Кромки+Пазы+Обработка → одна строка, qty суммируется.
 * Сохраняется первая строка группы (её id и прочие поля) и порядок первого
 * вхождения. Пазы и обработка — часть ключа (деталь с V-пазом/«Склейкой» ≠ без них).
 */
export function mergeIdenticalParts(parts: Part[]): Part[] {
  const groups = new Map<string, Part>();
  for (const p of parts) {
    const key = `${p.length}|${p.width}|${p.name}|${edgesKey(p.edges)}|${groovesKey(p.grooves)}|${p.processing}`;
    const existing = groups.get(key);
    if (existing) {
      existing.qty += p.qty;
    } else {
      groups.set(key, { ...p, edges: { ...p.edges }, grooves: { ...p.grooves } });
    }
  }
  return [...groups.values()];
}

/** Размеры листа для валидации: uniform → uniformSheet; mixed → максимум из вариантов. */
export function resolveSheetDims(cfg: SheetSizeConfig): { length: number; width: number } | null {
  if (cfg.mode === 'mixed') {
    const v = cfg.mixedVariants;
    if (!v) return null;
    return {
      length: Math.max(...v.map((s) => s.length)),
      width: Math.max(...v.map((s) => s.width)),
    };
  }
  if (cfg.uniformSheet) return { length: cfg.uniformSheet.length, width: cfg.uniformSheet.width };
  return null;
}

/**
 * Помещается ли деталь на лист с учётом обпила краёв (§5.1.10). Полезная зона =
 * лист − 2×обпил. Деталь помещается если влезает хотя бы в одной ориентации.
 * Неполная деталь (0 размер) и неопределённый лист → НЕ ошибка (помещается).
 */
export function partFitsSheet(part: Part, cfg: SheetSizeConfig, edgeOffset: number): boolean {
  const L = part.length;
  const W = part.width;
  if (L <= 0 || W <= 0) return true; // неполная — не валидируем (как прототип)
  const sheet = resolveSheetDims(cfg);
  if (!sheet) return true; // лист не задан — нечем проверять
  const usefulW = sheet.length - 2 * edgeOffset;
  const usefulH = sheet.width - 2 * edgeOffset;
  return (L <= usefulW && W <= usefulH) || (L <= usefulH && W <= usefulW);
}

/** Статус допустимости размера детали (ADR-0087). */
export type PartSizeStatus = 'ok' | 'too-small' | 'too-big';

/**
 * Допустимость размера детали (ADR-0087, решение владельца). Деталь недопустима,
 * если: (1) короткая сторона < `minPartSize` (новая проверка минимума; станочный
 * минимум, ЛДСП ~40мм) — `'too-small'`; либо (2) не помещается на лист ни в одной
 * ориентации (`!partFitsSheet`, максимум = лист − 2×обпил, БЕЗ kerf) — `'too-big'`.
 *
 * Неполная деталь (0 размер) — `'ok'` (не валидируется, как `partFitsSheet`).
 * `minPartSize <= 0` выключает проверку минимума. Валидация чисто web (до движка).
 */
export function partSizeStatus(
  part: Part,
  cfg: SheetSizeConfig,
  edgeOffset: number,
  minPartSize: number,
): PartSizeStatus {
  const L = part.length;
  const W = part.width;
  if (L <= 0 || W <= 0) return 'ok'; // неполная — не валидируем
  if (minPartSize > 0 && Math.min(L, W) < minPartSize) return 'too-small';
  if (!partFitsSheet(part, cfg, edgeOffset)) return 'too-big';
  return 'ok';
}
